import type { JSX } from "react";
import { useI18n } from "../../i18n";
import { useSyncStatus } from "../../hooks/useSyncStatus";

export function SyncStatus(): JSX.Element | null {
  const { t } = useI18n();
  const { status, failed, retryAll, discardAll } = useSyncStatus(undefined, 2000);

  const hasFailed = failed.length > 0;

  let badge: string;
  let tone: "lime" | "rust" | "mute";
  switch (status) {
    case "syncing":
      badge = t("status.syncSyncing");
      tone = "lime";
      break;
    case "offline":
      badge = t("status.syncOffline");
      tone = "rust";
      break;
    case "auth-required":
      badge = t("status.syncAuthRequired");
      tone = "rust";
      break;
    case "failed":
      badge = t("status.syncFailed", { count: failed.length });
      tone = "rust";
      break;
    case "idle":
    default:
      badge = t("status.syncIdle");
      tone = "mute";
      break;
  }

  const color =
    tone === "lime"
      ? "var(--color-lime)"
      : tone === "rust"
      ? "var(--color-blood)"
      : "var(--color-ink-mute)";

  return (
    <div
      data-testid="sync-status"
      className="flex items-center gap-3 border-b border-[var(--color-line)] px-4 py-2"
    >
      <span className="font-subhead text-xs uppercase tracking-[0.06em] text-[var(--color-ink-mute)]">
        {t("status.syncLabel")}
      </span>
      <span
        className="font-subhead text-xs uppercase tracking-[0.06em]"
        style={{ color }}
        data-testid="sync-status-badge"
      >
        {badge}
      </span>
      {hasFailed && (
        <div className="ml-auto flex gap-2">
          <button
            type="button"
            onClick={() => void retryAll()}
            data-testid="sync-retry"
            className="border border-[var(--color-line-strong)] px-3 py-1 font-subhead text-xs uppercase tracking-[0.06em] text-[var(--color-ink)] hover:bg-[var(--color-slab-2)]"
          >
            {t("status.syncRetry")}
          </button>
          <button
            type="button"
            onClick={() => void discardAll()}
            data-testid="sync-discard"
            className="border border-[var(--color-line-strong)] px-3 py-1 font-subhead text-xs uppercase tracking-[0.06em] text-[var(--color-ink)] hover:bg-[var(--color-slab-2)]"
          >
            {t("status.syncDiscard")}
          </button>
        </div>
      )}
    </div>
  );
}
